import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmFlex,
  CmHeadingPagination,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmScrollBox,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
  ReactSingleSelectInput,
  SelectForm,
  useCounter,
} from '@/components/uiParts';
import { LIMIT_PER_PAGE } from '@/domain/models/Pagination';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import { ommisionText } from '@/lib/util';
import {
  brandCategoryTableThs,
  MenuManagementBrandCategory,
} from '@/pages/menu-managements/categories/service';
import { HopeMenuManagementJobNotice } from '@/pages/menu-managements/components/Alert/HopeMenuManagementJobNotice';
import {
  MenuManagementSearchArea,
  SEARCH_AREA_ID,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementBrandCategoryList } from '@/services/BrandCategory/hooks/list/useMenuManagementBrandCategoryList';

const MenuManagementCategoriesPage: React.FC = (): JSX.Element => {
  const { managerAccountMe } = useManagerAccountMe();
  const history = useHistory();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [categories, setCategories] = useState<MenuManagementBrandCategory[]>(
    []
  );
  const [searchCount, setSearchCount] = useState(0);
  const categoryLength = categories.length;
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState<number>();

  const [targetAssociateBrandSelect, setTargetAssociateBrandSelect] =
    useState<SelectForm | null>({
      label: '',
      value: '',
    });

  const { count, decrement, increment, customizeCount } = useCounter(1);

  const [titleJa, setTitleJa] = useState<string>('');

  const getListQuery = {
    associateBrandId: targetAssociateBrandSelect?.value ?? '',
    currentPage: 1,
    limit: LIMIT_PER_PAGE,
    title: titleJa,
  } as const;

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const titleJa = e.target.value;
    setTitleJa(titleJa);

    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      setIsLoading(true);
      const { categories, count } = await useMenuManagementBrandCategoryList({
        ...getListQuery,
        currentPage: 1,
        title: titleJa,
      });
      customizeCount(1);
      setSearchCount(count);
      setCategories(categories);
      setIsLoading(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: targetAssociateBrandSelect,
    options: associateBrandSelectOptions,
    onChange: async (associateBrand: SelectForm | null) => {
      if (
        associateBrand === null ||
        associateBrand.value === targetAssociateBrandSelect?.value
      )
        return;
      const associateBrandId = associateBrand.value;
      setIsLoading(true);
      setTargetAssociateBrandSelect(associateBrand);
      const { categories, count } = await useMenuManagementBrandCategoryList({
        ...getListQuery,
        currentPage: 1,
        associateBrandId,
      });
      customizeCount(1);
      setSearchCount(count);
      setCategories(categories);
      setIsLoading(false);
    },
    onReset: () => {
      setTargetAssociateBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const toFirstPage = async () => {
    setIsLoading(true);
    const { categories } = await useMenuManagementBrandCategoryList({
      ...getListQuery,
    });
    setCategories(categories);
    customizeCount(1);
    setIsLoading(false);
  };

  const toPreviousPage = async () => {
    setIsLoading(true);
    const previousPage = count - 1;
    const { categories } = await useMenuManagementBrandCategoryList({
      ...getListQuery,
      currentPage: previousPage,
    });
    setCategories(categories);
    decrement();
    setIsLoading(false);
  };

  const toNextPage = async () => {
    setIsLoading(true);
    const nextPage = count + 1;
    const { categories } = await useMenuManagementBrandCategoryList({
      ...getListQuery,
      currentPage: nextPage,
    });
    setCategories(categories);
    increment();
    setIsLoading(false);
  };

  const toLastPage = async () => {
    setIsLoading(true);
    const lastPage = Math.ceil(searchCount / LIMIT_PER_PAGE);
    const { categories } = await useMenuManagementBrandCategoryList({
      ...getListQuery,
      currentPage: lastPage,
    });
    setCategories(categories);
    customizeCount(lastPage);
    setIsLoading(false);
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, SEARCH_AREA_ID]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const associateBrands = await useAssociateBrandList();
      const associateBrandOptions = associateBrands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id.toString(),
        };
      });
      setAssociateBrandSelectOptions(associateBrandOptions);
      const { associateBrand } = LocalStorageHandler.get('menuManagement');
      const initialAssociateBrand =
        associateBrand !== undefined
          ? associateBrand
          : associateBrandOptions[0];
      setTargetAssociateBrandSelect(initialAssociateBrand);
      LocalStorageHandler.set('menuManagement', {
        associateBrand: initialAssociateBrand,
      });

      const { categories, count } = await useMenuManagementBrandCategoryList({
        ...getListQuery,
        associateBrandId: initialAssociateBrand.value,
        currentPage: 1,
      });
      customizeCount(1);
      setSearchCount(count);
      setCategories(categories);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout
      title={routes.menuManagementCategories.title}
      alert={<HopeMenuManagementJobNotice />}
      {...{ associateBrandSelectInput }}
    >
      <CmBox>
        <MenuManagementSearchArea
          targetInputLabel="カテゴリ"
          newPagePath={'/menu-managements/categories/new'}
          itemValue={titleJa}
          {...{
            associateBrandSelectInput,
            searchCount,
            handleInputTitleJa,
          }}
        />
        {categoryLength > 0 ? (
          <CmCard
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 20px)`}
          >
            <CmFlex flexDirection="row-reverse" my="5px">
              <CmHeadingPagination
                totalNum={searchCount}
                currentPage={count}
                LimitPerPage={LIMIT_PER_PAGE}
                {...{ toFirstPage, toNextPage, toPreviousPage, toLastPage }}
              />
            </CmFlex>
            <CmScrollBox>
              <CmBaseTable ths={brandCategoryTableThs}>
                {categories.map((category, index) => {
                  const memo = nullToEmptyString(category.memo);
                  const updatedAt = DateDomainService.datetimeFormat(
                    new Date(category.updated_at)
                  );
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmLink
                          color="blue.500"
                          to={`/menu-managements/categories/${category.id}/edit`}
                        >
                          <CmTooltip label={category.title.translation.ja}>
                            {ommisionText(category.title.translation.ja, 20)}
                          </CmTooltip>
                        </CmLink>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={memo}>
                          {ommisionText(memo, 20)}
                        </CmTooltip>
                      </CmTd>
                      <CmTd textAlign="center">
                        <CmTooltip label={updatedAt}>{updatedAt}</CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                検索結果に一致するカテゴリが見当たりませんでした。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </PageLayout>
  );
};

export default MenuManagementCategoriesPage;
